import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, MapPin, Phone } from "lucide-react";
import { Nav } from "@/components/Nav";
import { Footer } from "@/components/Footer";
import { Reveal } from "@/components/Reveal";
import { SITE } from "@/constants/site";

const AREAS = [
  {
    name: "Hanover, PA",
    slug: "hanover-pa",
    tag: "Home base",
    text: "Our home turf. Same-day pickups across Hanover — from downtown row homes to farms on the edge of town. Furniture, appliances, cleanouts, and light demo.",
    towns: "Hanover · McSherrystown · Penn Township · Conewago Township",
  },
  {
    name: "York County",
    slug: "york-pa",
    tag: "Same-day available",
    text: "Full-service junk removal across York County — garage cleanouts, estate cleanouts, yard debris, and office cleanouts for local businesses.",
    towns: "York · Spring Grove · Red Lion · Dallastown · Shrewsbury",
  },
  {
    name: "Lancaster County",
    slug: "lancaster-pa",
    tag: "By appointment",
    text: "Serving Lancaster County by appointment. Moving prep, renovation debris, furniture and appliance removal — one call and it's gone.",
    towns: "Lancaster · Lititz · Manheim · Ephrata · Columbia",
  },
  {
    name: "Adams County",
    slug: "gettysburg-pa",
    tag: "Same-day available",
    text: "From Gettysburg rentals to rural barns, we haul it all across Adams County with upfront pricing and a swept-clean finish.",
    towns: "Gettysburg · Littlestown · New Oxford · Abbottstown",
  },
  {
    name: "Carroll County",
    slug: "westminster-md",
    tag: "By appointment",
    text: "Serving northern Carroll County — home cleanouts, yard waste, and light demolition for homeowners and property managers.",
    towns: "Westminster · Taneytown · Manchester · Hampstead",
  },
];

export default function ServiceAreas() {
  useEffect(() => {
    document.title = "Service Areas — Take The Leap Junk Removal | Hanover, York, Lancaster, Adams, Carroll";
    window.scrollTo({ top: 0 });
  }, []);

  return (
    <div data-testid="service-areas-page" className="bg-ink font-sans text-zinc-100 antialiased">
      <div className="noise-overlay" aria-hidden="true" />
      <Nav />
      <main className="pt-24">
        <section className="mx-auto max-w-7xl px-6 pb-10 pt-16 md:px-10 md:pt-24">
          <Reveal>
            <p className="mb-4 text-xs font-bold uppercase tracking-[0.35em] text-acid">Service areas</p>
            <h1 className="max-w-4xl font-display text-5xl font-black italic uppercase leading-[0.9] tracking-tighter sm:text-6xl lg:text-7xl">
              Proudly hauling across <span className="text-acid">Hanover & the counties around it.</span>
            </h1>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-zinc-400">
              Based in Hanover, PA — running routes through York, Lancaster, Adams, and Carroll Counties.
              If you’re nearby and don’t see your town, call anyway. If we can reach you, we’ll haul for you.
            </p>
          </Reveal>
        </section>

        <section className="mx-auto max-w-7xl px-6 pb-24 md:px-10">
          <div className="border-t border-seam">
            {AREAS.map((a, i) => (
              <Reveal key={a.name} delay={i * 0.05}>
                <div
                  data-testid={`area-card-${i + 1}`}
                  className="group grid gap-6 border-b border-seam py-10 transition-colors duration-300 hover:bg-coal md:grid-cols-12 md:items-center md:px-6"
                >
                  <span className="text-outline-acid font-display text-6xl font-black italic leading-none md:col-span-2">
                    0{i + 1}
                  </span>
                  <div className="md:col-span-4">
                    <h2 className="font-display text-3xl font-extrabold italic uppercase tracking-tight transition-colors duration-200 group-hover:text-acid">
                      {a.name}
                    </h2>
                    <span className="mt-3 inline-block border border-seam px-3 py-1 text-[10px] font-bold uppercase tracking-[0.25em] text-zinc-400">
                      {a.tag}
                    </span>
                  </div>
                  <div className="md:col-span-6">
                    <p className="text-sm leading-relaxed text-zinc-400 md:text-base">{a.text}</p>
                    <p className="mt-3 flex items-start gap-2 text-xs font-semibold uppercase tracking-[0.15em] text-zinc-500">
                      <MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0 text-acid" />
                      {a.towns}
                    </p>
                    {a.slug && (
                      <Link
                        data-testid={`area-link-${a.slug}`}
                        to={`/areas/${a.slug}`}
                        className="mt-4 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.25em] text-acid hover:underline"
                      >
                        Junk removal in {a.name.replace(" County", "")} <ArrowUpRight className="h-3.5 w-3.5" />
                      </Link>
                    )}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </section>

        <section className="border-t border-seam bg-coal">
          <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-8 px-6 py-16 md:px-10">
            <Reveal>
              <h2 className="font-display text-3xl font-black italic uppercase tracking-tight sm:text-4xl">
                In one of these areas? <span className="text-acid">Let’s clear it out.</span>
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="flex flex-wrap gap-4">
                <Link
                  data-testid="areas-quote-btn"
                  to="/#quote"
                  className="group flex items-center gap-2 bg-acid px-8 py-4 font-display text-xl font-extrabold italic uppercase tracking-tight text-black transition-colors duration-200 hover:bg-white"
                >
                  Get a free quote
                  <ArrowUpRight className="h-5 w-5 transition-transform duration-200 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                </Link>
                <a
                  data-testid="areas-call-btn"
                  href={`tel:${SITE.phoneTel}`}
                  className="flex items-center gap-3 border border-zinc-700 px-8 py-4 font-display text-xl font-extrabold italic uppercase tracking-tight text-white transition-colors duration-200 hover:border-acid hover:text-acid"
                >
                  <Phone className="h-5 w-5" />
                  {SITE.phoneDisplay}
                </a>
              </div>
            </Reveal>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
